import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  VehicleDispositionViewModel,
  VehicleDispositionViewModelService,
} from 'src/models/vehicle-disposition-view-model';
import { VehicleEntityFetchService } from 'src/store/generated/client-sync.generated';
import { VehicleRecord, VehicleViewModelBase } from 'src/store/generated/vehicle-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { QueryOptions } from 'src/store/fetch-services';
import { VehicleMakeViewModel, VehicleMakeViewModelService } from './vehicle-make-view-model';
import { VehicleModelViewModel, VehicleModelViewModelService } from './vehicle-model-view-model';

export function vehicleDisplayName({
  makeName,
  modelName,
  registrationNumber,
}: {
  makeName?: string | null;
  modelName?: string | null;
  registrationNumber: string;
}) {
  if (makeName != null && modelName != null) {
    return `${registrationNumber} (${makeName} ${modelName})`;
  } else if (makeName != null) {
    return `${registrationNumber} (${makeName})`;
  } else if (modelName != null) {
    return `${registrationNumber} (${modelName})`;
  }
  return registrationNumber;
}

export class VehicleViewModel extends VehicleViewModelBase {
  get displayName(): string {
    return vehicleDisplayName({
      makeName: this.make != null ? this.make.name : undefined,
      modelName: this.model != null ? this.model.name : undefined,
      registrationNumber: this.registrationNumber,
    });
  }
  constructor(
    vehicle: VehicleRecord,
    public readonly model: VehicleModelViewModel | undefined,
    public readonly make: VehicleMakeViewModel | undefined,
    public readonly vehicleDisposition: VehicleDispositionViewModel | undefined,
  ) {
    super(vehicle);
  }
}

@Injectable({ providedIn: 'root' })
export class VehicleViewModelService {
  constructor(
    private readonly _vehicleEntityFetchService: VehicleEntityFetchService,
    private readonly _vehicleModelViewModel: VehicleModelViewModelService,
    private readonly _vehicleMakeViewModel: VehicleMakeViewModelService,
    private readonly _vdVm: VehicleDispositionViewModelService,
  ) {}

  public getById(id: string, options?: QueryOptions): Observable<VehicleViewModel | undefined> {
    return this._vehicleEntityFetchService.getById(id, options).pipe(
      undefinedOrSwitchMap((vehicle) => {
        const model$ =
          vehicle.modelId != null
            ? this._vehicleModelViewModel.getById(vehicle.modelId, options)
            : of(undefined);
        const make$ =
          vehicle.makeId != null
            ? this._vehicleMakeViewModel.getById(vehicle.makeId, options)
            : of(undefined);
        const vd$ = this._vdVm.getByVehicleId(vehicle.id, options);

        return combineLatest([model$, vd$, make$]).pipe(
          map(([model, disposition, make]) => {
            const vm = new VehicleViewModel(vehicle, model, make, disposition);

            return vm;
          }),
        );
      }),
    );
  }
}
